import { gql, useQuery } from "@apollo/client"
import { RemoteWorkCostsFragment } from "../fragments/RemoteWorkCostsFragment"
import { STAFF_EMPLOYMENT_PAGE_QUERY_NAME } from "../constants/employmentPage"
import { Employment, Engagement } from "@oysterhr/schema"
import { TodoTasksTaskFragment } from "../fragments/TodoTasksTaskFragment"

export const DirectDebitForDepositInvoiceFragment = gql`
  fragment DirectDebitForDepositInvoiceFragment on Company {
    directDebitConstraint {
      blocked
    }
    directDebitAuthorized
    directDebitAvailable
    directDebitEnforced
    invoicingSettings {
      depositBeforeLiability
    }
  }
`

/* Remove this when PersonalInformationTab is refactored to own its query */
const PERSONAL_INFORMATION_FRAGMENT = gql`
  fragment EmploymentPersonalInformationFragment on Employment {
    coEmployee {
      email
      id
      livingAddress {
        addressLine1
        addressLine2
        city
        complete
        countryCode
        createdAt
        id
        postalCode
        region
        syncedFields
        pushedFields
      }
      mailingAddress {
        addressLine1
        addressLine2
        city
        complete
        countryCode
        createdAt
        id
        postalCode
        region
        syncedFields
        pushedFields
      }
      name
      preferredNameActual
      residenceCountryCode
      syncedFields
      pushedFields
      userPersonalInfo {
        citizenships
        dateOfBirth
        phoneCountryCode
        phoneNumber
        syncedFields
        pushedFields
      }
    }
  }
`

/* Remove this when PersonalInformationTab is refactored to own its query */
const PERSONAL_INFORMATION_CONTRACT_FRAGMENT = gql`
  fragment PersonalInformationContractFragment on Engagement {
    contract {
      individualAddress {
        addressLine1
        addressLine2
        city
        complete
        countryCode
        createdAt
        id
        postalCode
        region
        syncedFields
        pushedFields
      }
    }
  }
`

/* Remove this when TabBenefits query is updated */
const SUGGESTED_BENEFITS_FRAGMENT = gql`
  fragment SuggestedBenefitsFragment on Employment {
    benefitsDescription
    suggestedBenefitPlans {
      id
      name
      provider {
        name
      }
      priceEstimate {
        currencies {
          subunit
          currencyCode
        }
      }
    }
  }
`

/* Most of this information is being used in EmploymentSwitcher */
const EMPLOYMENT_FRAGMENT = gql`
  ${PERSONAL_INFORMATION_FRAGMENT}
  ${SUGGESTED_BENEFITS_FRAGMENT}
  ${RemoteWorkCostsFragment}
  fragment EmploymentFragment on Employment {
    isPayrollTeamMember
    isUsaExemptEmployee
    agreementsFields
    company {
      id
      name
      paymentCurrencyCode
      ...DirectDebitForDepositInvoiceFragment
      depositInvoiceRequired
    }
    countryCode
    subdivisionCode
    createdAt
    employmentType
    endDate
    terminationDate
    documents {
      id
    }
    engagementType
    employerOfRecord {
      id
    }
    restrictiveCovenants {
      confidentiality
      nonCompetition
      nonSolicitation
    }
    depositBeforeLiability
    hiring {
      banners
      id
      employmentAgreementsInfo {
        generationType
      }
      hiringProcess {
        # This is only used to check if this object is available or not
        # The idea is that this replaces a feature flag
        completedAt
        staffTasks {
          ...TodoTasksTaskDetails
        }
      }
      agreements {
        agreementType
        state
        isESignEnabled
        isAutomated
        automation {
          state
        }
        envelope {
          id
          recipients {
            party
            email
          }
          events {
            eventType
            createdAt
          }
        }
      }
      envelopes {
        id
        state
        envelopeType
        isUpdateNeeded
        automationState
        currentSigners {
          party
          status
        }
        recipients {
          party
          recipientType
          status
          email
          name
        }
      }
      companyProcess {
        currentStep {
          name
          progress
        }
        steps {
          name
          progress
        }
      }
      employeeProcess {
        currentStep {
          name
          progress
        }
        steps {
          name
          progress
        }
      }
      agreementsSynced
      isAgreementESignEnabled
      isOsaSigned
      isEmploymentAgreementSigned
      isEmploymentAgreementsEnvelopePartiallySigned
      onboardingForm {
        completedAt
        id
        state
      }
      envelopes {
        id
        recipients {
          party
          recipientType
          status
          email
          name
        }
      }
    }
    id
    invitation {
      name
      sentAt
      usedTokenAt
    }
    lastSubmittedOn
    partTimeDaysPerWeek
    partTimeHoursPerDay
    personalInfo {
      email
      fullLegalName
    }
    role
    jobDescription
    minimumQualification
    dailyResponsibility
    primaryDuty
    decisionMakingAuthority
    salaryAmount
    salaryCurrencyCode
    startDate
    state
    employmentComplianceCheck
    employmentComplianceDetails
    isEditable
    # Remove this when PersonalInformationTab is refactored
    ...EmploymentPersonalInformationFragment
    # Remove this when TabBenefits query is updated
    ...SuggestedBenefitsFragment
    ...RemoteWorkCostsFragment
  }
  ${TodoTasksTaskFragment}
  ${DirectDebitForDepositInvoiceFragment}
`

export const STAFF_EMPLOYMENT_PAGE_QUERY = gql`
  ${PERSONAL_INFORMATION_CONTRACT_FRAGMENT}
  ${EMPLOYMENT_FRAGMENT}
  ${RemoteWorkCostsFragment}
  query ${STAFF_EMPLOYMENT_PAGE_QUERY_NAME}($engagementId: ID!) {
    engagement(engagementId: $engagementId) {
      # Remove PersonalInformationContractFragment when PersonalInformationTab is updated to own it's query
      ...PersonalInformationContractFragment
      createdAt
      engagedAt
      id
      name
      state
      startDate
      engagementType
      cutoffMonthDay
      languageCode
      isPayrollTeamMember
      teamMemberName
      changeReasons {
          changeType
          id
          reasonMessage
          reasonType
        }
      pendingOrActiveSubscription {
        id
      }
      activeOrPendingSubscription {
        id
        plan {
          id
          name
          product
          billingFrequencyInMonths
          planPrice(engagementId: $engagementId) {
            amount {
              subunit
              currencyCode
            }
          }
        }
        supersededBy {
          id
          plan {
            id
            name
            product
            billingFrequencyInMonths
          }
          startsOn
        }
      }
      company {
        id
        paymentsSettings {
          restricted
          flaggedAsFraudster
          badDebtCategory
        }
        authorizations {
          actions
        }
      }
      engagementChanges {
        id
      }
      pendingEngagementChange {
        id
        state
        hasAmendmentFlowAutomatable
        availableActions
      }
      termination {
        id
        state
        paidInFullStatus
        offboardingStepsTerminations {
          id
          status
        }
        agreements {
          id
          state
          agreementType
        }
      }
      timeTrackingToolOverride
      eligibleToOnCall
      eligibleToNightWork
      onCallEnabled
      nightWorkEnabled
      carryoverExpirationPeriodPolicy
      carryoverExpiringPeriodInMonthsAdjustment
      entitlementCountryEnabled
      entitlementCountryCarryoverExpiringPeriodInMonths
      carryoverAmountLimitationPolicy
      flexibleWorkArrangementAvailable
      flexibleWorkArrangement
      canOverrideCarryoverExpiringPeriod
      carryoverDate
      carryoverExpirationDate
      carryoverExpirationPeriodPolicy
      carryoverAnnualLimit
      entitlementCountryCarryoverAnnualLimit
      maxDaysByCoefficientOfPaidHolidayDays
      maxCarryoverDays
      minDaysToUse
      paidHolidayDays
      accrualCycleSpans {
        startDate
        endDate
      }
      isPayrollTeamMember
      globalPayrollOffboardingCountry {
        currency
      }
    }
    employment(employmentId: $engagementId) {
      ...EmploymentFragment
      ...RemoteWorkCostsFragment
    }
  }
`

export const useEmploymentPage = (id: string) =>
  useQuery<{ engagement: Engagement; employment: Employment }, { engagementId: string }>(
    STAFF_EMPLOYMENT_PAGE_QUERY,
    {
      variables: {
        engagementId: id,
      },
    },
  )
